import { Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import * as R from 'ramda';
import { useEffect, useRef } from 'react';
import { matchPath, Navigate, Outlet, useLocation } from 'react-router-dom';

import { Routes } from '@/constants';
import { AppFooter, PageSidebar } from '@/containers';
import { AnalyticsService } from '@/services';
import {
  selectIsLpsListView,
  selectSessionUserId,
  useAppSelector,
} from '@/store';
import { appStyles } from '@/theme';

import {
  LearningPathsSidebar,
  SidebarLeafItem,
  useLearningPathsData,
  useSidebarItems,
} from './components';

const SIDEBAR_BREAKPOINT = 'lg';

export const LearningPaths = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(SIDEBAR_BREAKPOINT));
  const { pathname } = useLocation();

  const isListView = useAppSelector(selectIsLpsListView);
  const userId = useAppSelector(selectSessionUserId);

  const sidebarItems = useSidebarItems();
  const { isLoading, recommendedLps, sortedInProgressLps } =
    useLearningPathsData();

  const hasLoggedPageViewRef = useRef(false);

  const isLpDetailsRoute = !!matchPath(Routes.LEARNING_PATH, pathname);

  const mobilePillItems: SidebarLeafItem[] = [
    sidebarItems.inProgress,
    sidebarItems.recommended,
    sidebarItems.allTopics,
    ...sidebarItems.allTopics.children,
  ].filter((item): item is SidebarLeafItem => item !== undefined);

  useEffect(() => {
    if (isLoading || hasLoggedPageViewRef.current) return;
    hasLoggedPageViewRef.current = true;
    AnalyticsService.learningPathsPageViewed({
      inProgressPathsCount: sortedInProgressLps.length,
      recommendedPathsCount: recommendedLps.length,
      viewMode: isListView ? 'list' : 'grid',
      pathname,
    });
  }, [isLoading, recommendedLps, sortedInProgressLps, isListView, pathname]);

  if (!userId && pathname === Routes.LEARNING_PATHS) {
    return <Navigate to={Routes.LEARNING_PATHS_TOPICS} replace />;
  }

  return (
    <Box
      sx={{
        mx: theme => R.map(R.negate, theme.dimensions.appLayout.px),
        my: theme => R.map(R.negate, theme.dimensions.appLayout.py),
        py: theme => theme.dimensions.appLayout.py,
        px: theme => theme.dimensions.appLayout.px,
        backgroundColor: 'grey25',
        minHeight: theme =>
          `calc(100dvh - ${theme.dimensions.header.height}px)`,
      }}>
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <PageSidebar>
          <LearningPathsSidebar items={sidebarItems} />
        </PageSidebar>
      </Box>
      <Box
        sx={[
          appStyles.limitPageWidth,
          ({ dimensions }) => ({
            pl: { [SIDEBAR_BREAKPOINT]: dimensions.pageSidebar.width + 'px' },
          }),
        ]}>
        {!isDesktop && !isLpDetailsRoute && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mb: 2,
              overflowX: 'scroll',
              mx: theme => R.map(R.negate, theme.dimensions.appLayout.px),
              px: theme => theme.dimensions.appLayout.px,
            }}>
            {mobilePillItems.map(item => (
              <Chip
                key={item.label}
                label={item.label}
                variant={item.isSelected ? 'filled' : 'outlined'}
                onClick={item.onClick}
                sx={{
                  borderColor: 'primary.main',
                  color: item.isSelected ? 'text.contrast' : 'primary.main',
                  backgroundColor: item.isSelected
                    ? 'primary.main'
                    : 'common.white',
                  '&:hover, &:focus, &:focus-visible, &:active, &.Mui-focusVisible':
                    {
                      backgroundColor: item.isSelected
                        ? 'primary.main'
                        : 'common.white',
                    },
                }}
              />
            ))}
          </Box>
        )}
        <Outlet />
        <AppFooter compact backgroundColor="grey25" />
      </Box>
    </Box>
  );
};
